/**
 * Handles the add-to-cart operation.
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false;
        this.error = false;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        this.addCartButton = this.querySelector('button[type="submit"]');
        this.cartDrawer = document.getElementById("Cart-Drawer");
        if (this.cartDrawer && !this.cartDrawer.hasAttribute("data-status-silence")) {
          if (this.addCartButton) {
            this.addCartButton.setAttribute("aria-haspopup", "dialog");
            this.addCartButton.setAttribute("aria-expanded", "false");
            this.addCartButton.setAttribute("aria-controls", "Cart-Drawer");
          }
        }
        this.errorMessageWrapper = document.getElementById(`Product-Form-Error-Message-${this.dataset.section}`);
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      async onSubmitHandler(event) {
        if (this.isloading) return;
        event.preventDefault();
        if (this.addCartButton.hasAttribute("disabled")) return;

        this.handleErrorMessage();
        this.startLoading();

        const config = {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
        };
        
        const formData = new FormData(this.form);
        const productInfo = this.form.closest('product-info');
        
        const finalFormData = new FormData();
        finalFormData.append('sections', this.getSectionsToRender().map(s => s.section).join(','));
        finalFormData.append('sections_url', window.location.pathname);

        let mainItemIndex = 0;
        finalFormData.append(`items[${mainItemIndex}][id]`, formData.get('id'));
        finalFormData.append(`items[${mainItemIndex}][quantity]`, formData.get('quantity'));

        // 1. Aggiungi proprietà generiche dal form (escluse quelle speciali)
        for (const [key, value] of formData.entries()) {
            if (key.startsWith('properties') && value) {
                const match = key.match(/^properties\[(.*?)\]/);
                if (match && match[1]) {
                    const propName = match[1];
                    // Escludiamo Codice colore qui per gestirlo manualmente e con priorità dopo
                    // Escludiamo anche i campi di servizio
                    if (propName !== '_surcharge_variant_id' && propName !== 'Codice colore') {
                        finalFormData.append(`items[${mainItemIndex}][properties][${propName}]`, value);
                    }
                }
            }
        }

        // 2. Recupera esplicitamente il Codice Colore dai selettori personalizzati (che sono fuori dal form)
        // Cerchiamo input con il nome corretto all'interno del contenitore del prodotto
        let selectedColorCode = null;
        if (productInfo) {
            const colorInputs = productInfo.querySelectorAll('input[name="properties[Codice colore]"]');
            colorInputs.forEach(input => {
                if (input.value) {
                    selectedColorCode = input.value;
                }
            });
        }

        // Se abbiamo trovato un codice colore, aggiungilo
        if (selectedColorCode) {
            finalFormData.append(`items[${mainItemIndex}][properties][Codice colore]`, selectedColorCode);
        } else {
            // Fallback: controlla se era nel formData originale (per selettori standard o altri casi)
            const formColor = formData.get('properties[Codice colore]');
            if (formColor) {
                finalFormData.append(`items[${mainItemIndex}][properties][Codice colore]`, formColor);
            }
        }

        // 3. Gestione Sovrapprezzi (ATS e Rococo)
        let itemIndex = 1;
        if (productInfo) {
            const surchargeInputs = productInfo.querySelectorAll('[name="properties[_surcharge_variant_id]"]');
            
            surchargeInputs.forEach(surchargeInput => {
                if (surchargeInput.value) {
                    const mainProductId = formData.get('id');
                    const quantity = formData.get('quantity');
                    
                    finalFormData.append(`items[${itemIndex}][id]`, surchargeInput.value);
                    finalFormData.append(`items[${itemIndex}][quantity]`, quantity);
                    finalFormData.append(`items[${itemIndex}][properties][_linked_to]`, mainProductId);
                    finalFormData.append(`items[${itemIndex}][properties][_sovrapprezzo_hidden]`, 'true');

                    if (surchargeInput.dataset.bucket) {
                        finalFormData.append(`items[${mainItemIndex}][properties][Categoria colore]`, surchargeInput.dataset.bucket);
                    }
                    if (surchargeInput.dataset.surchargeEur) {
                        finalFormData.append(`items[${mainItemIndex}][properties][Sovrapprezzo EUR]`, surchargeInput.dataset.surchargeEur);
                    }
                    
                    itemIndex++;
                }
            });
        }

        config.body = finalFormData;

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }
            this.error = false;
            SectionDynamicUpdate.updateSections(this.getSectionsToRender(), response.sections);
            webvista.publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-form", productVariantId: formData.get("id") });
            if (this.cartDrawer) {
              this.cartDrawer.show(this.addCartButton);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.accessibilityStrings.unknownError);
          })
          .finally(() => {
            this.endLoading();
          });
      }

      getSectionsToRender() {
        return [
          { id: "Cart-Icon-Bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
          { id: "Cart-Drawer", section: "cart-drawer", selector: ".cart-count" },
          { id: "Cart-Drawer", section: this.cartDrawer.dataset.section, selector: "#Cart-Drawer-Details" }
        ];
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) {
          this.errorMessageWrapper.textContent = errorMessage;
        }
      }

      startLoading() {
        this.isloading = true;
        this.addCartButton.setAttribute("disabled", true);
        this.addCartButton.classList.add("loading");
      }

      endLoading() {
        this.isloading = false;
        this.addCartButton.removeAttribute("disabled");
        this.addCartButton.classList.remove("loading");
      }
    }
  );
}
